import "./tiles.css";

/**
 * One count, with a label over it and a line of context under it.
 *
 * `note` is a sentence about how the number is made up — "3 collections, 1
 * shared" — and is null rather than invented when there is nothing true to say.
 * Never a trend: nothing in this registry records what a count was last month.
 */
export interface Tile {
  key: string;
  label: string;
  value: string;
  note: string | null;
}

/**
 * The row of counts at the top of a workspace's front page.
 *
 * Lifted out of `dashboard/DashboardPanels.tsx`, where it had been since the
 * collector's dashboard was the only screen with one. The agent's
 * representations list opens with the same object for the same reason — how
 * much is here, cut four ways, before the list itself — and a second copy of it
 * would have been the fifth `InitialsTile` all over again: two rows of tiles
 * that drift in padding and type until they stop reading as one system.
 *
 * A `<ul>`, because it is a list of counts and not a table: there is one row,
 * and the tiles have no relation to each other beyond being about the same
 * subject.
 */
export function TileRow({ tiles }: { tiles: Tile[] }) {
  return (
    <ul className="tiles">
      {tiles.map((tile) => (
        <li key={tile.key} className="tiles__tile">
          <span className="tiles__label">{tile.label}</span>
          <span className="tiles__value">{tile.value}</span>
          {/* An absent note leaves the space rather than closing it up, so the
              tiles keep one baseline across the row. */}
          <span className="tiles__note">{tile.note ?? " "}</span>
        </li>
      ))}
    </ul>
  );
}
